import { useState, useId } from 'react'
import { Input } from '@components/ui/Input'
import { Button } from '@components/ui/Button'
import { Car, Search, X } from 'lucide-react'
import { cn } from '@utils/cn'

export interface VehicleData {
  matricula: string
  marca: string
  modelo: string
  ano: string
  km: string
  combustivel: string
  /** AAAA-MM-DD (opcional), alinhado à data da última inspeção no veículo */
  ultimaInspecao: string
}

const COMBUSTIVEIS = [
  { value: 'GASOLINE', label: 'Gasolina' },
  { value: 'DIESEL', label: 'Gasóleo/Diesel' },
  { value: 'HYBRID_GASOLINE', label: 'Híbrido (Gasolina)' },
  { value: 'HYBRID_DIESEL', label: 'Híbrido (Diesel)' },
  { value: 'ELECTRIC', label: 'Elétrico' },
  { value: 'GAS', label: 'GNV/GPL' },
]

const MARCAS_COMUNS = [
  'Ford', 'Renault', 'Peugeot', 'Citroën', 'Volkswagen', 'BMW', 'Mercedes',
  'Audi', 'SEAT', 'Opel', 'Fiat', 'Toyota', 'Honda', 'Nissan', 'Mazda',
  'Hyundai', 'Kia', 'Škoda', 'Volvo', 'Mini', 'Smart',
]

interface VehicleDataFormProps {
  readonly onSubmit: (data: VehicleData) => void | Promise<void>
  readonly onRegistrationPlateSearch?: (registrationPlate: string) => Promise<VehicleData | null>
  readonly isLoading?: boolean
  readonly initialData?: Partial<VehicleData>
  readonly disabled?: boolean
  readonly onClose?: () => void
}

export function VehicleDataForm({
  onSubmit,
  onRegistrationPlateSearch,
  isLoading = false,
  initialData = {},
  disabled = false,
  onClose,
}: Readonly<VehicleDataFormProps>) {
  const [formData, setFormData] = useState<VehicleData>({
    matricula: initialData.matricula || '',
    marca: initialData.marca || '',
    modelo: initialData.modelo || '',
    ano: initialData.ano || '',
    km: initialData.km || '',
    combustivel: initialData.combustivel || '',
    ultimaInspecao: initialData.ultimaInspecao || '',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof VehicleData, string>>>({})
  const [isSearching, setIsSearching] = useState(false)

  const matriculaId = useId()
  const marcaId = useId()
  const modeloId = useId()
  const anoId = useId()
  const kmId = useId()
  const combustivelId = useId()
  const ultimaInspecaoId = useId()

  const validateMatricula = (matricula: string): boolean => {
    return /^\d{2}-[A-Z]{2}-\d{2}$/.test(matricula.toUpperCase())
  }

  const validateAno = (ano: string): boolean => {
    const year = Number.parseInt(ano, 10)
    return !Number.isNaN(year) && year >= 1900 && year <= new Date().getFullYear() + 1
  }

  const formatMatricula = (value: string): string => {
    const cleaned = value.replaceAll(/[^A-Z0-9]/gi, '').toUpperCase()
    if (cleaned.length <= 2) return cleaned
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
    if (cleaned.length <= 6)
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4)}`
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 6)}`
  }

  const handleRegistrationPlateSearch = async () => {
    if (!formData.matricula || !validateMatricula(formData.matricula)) {
      setErrors({ matricula: 'Matrícula deve ter formato XX-XX-XX' })
      return
    }

    if (!onRegistrationPlateSearch) return

    setIsSearching(true)
    setErrors({})
    try {
      const found = await onRegistrationPlateSearch(formData.matricula)
      if (found) {
        setFormData(found)
        setErrors({})
      } else {
        setErrors({ matricula: 'Veículo não encontrado com esta matrícula' })
      }
    } catch (error) {
      setErrors({ matricula: 'Erro ao pesquisar veículo' })
    } finally {
      setIsSearching(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Partial<Record<keyof VehicleData, string>> = {}

    if (!formData.matricula.trim()) {
      newErrors.matricula = 'Matrícula é obrigatória'
    } else if (!validateMatricula(formData.matricula)) {
      newErrors.matricula = 'Formato inválido (ex: 11-AA-11)'
    }

    if (formData.ano.trim() && !validateAno(formData.ano)) {
      newErrors.ano = 'Ano inválido'
    }

    if (formData.km.trim()) {
      const kmNum = Number.parseInt(formData.km.replace(/\s/g, ''), 10)
      if (Number.isNaN(kmNum) || kmNum < 0) {
        newErrors.km = 'Quilometragem inválida'
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    onSubmit(formData)
  }

  const currentYear = new Date().getFullYear()
  const startYear = 1960
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i)

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white rounded-lg border border-gray-200 relative">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Car className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Dados do Veículo</h3>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500 -mt-2 mb-2">
        Obrigatório: matrícula. Marca, modelo, ano, km, combustível e data da última inspeção são opcionais.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor={matriculaId} className="block text-sm font-medium text-gray-700 mb-1">
            Matrícula <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                id={matriculaId}
                type="text"
                value={formData.matricula}
                onChange={(e) => {
                  const formatted = formatMatricula(e.target.value)
                  setFormData((prev) => ({ ...prev, matricula: formatted }))
                  if (errors.matricula) setErrors((prev) => ({ ...prev, matricula: undefined }))
                }}
                placeholder="11-AA-11"
                maxLength={8}
                error={errors.matricula}
                className="w-full uppercase"
                disabled={disabled}
              />
            </div>
            {onRegistrationPlateSearch && (
              <Button
                type="button"
                onClick={handleRegistrationPlateSearch}
                disabled={isSearching || !formData.matricula || !validateMatricula(formData.matricula) || disabled}
                className="px-4"
              >
                <Search className="w-4 h-4" />
              </Button>
            )}
          </div>
          {onRegistrationPlateSearch && (
            <p className="mt-1 text-xs text-gray-500">Pesquisa opcional com matrícula completa.</p>
          )}
        </div>

        <div>
          <label htmlFor={marcaId} className="block text-sm font-medium text-gray-700 mb-1">
            Marca <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <div className="relative">
            <Input
              id={marcaId}
              type="text"
              value={formData.marca}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, marca: e.target.value }))
                if (errors.marca) setErrors((prev) => ({ ...prev, marca: undefined }))
              }}
              placeholder="Ford"
              error={errors.marca}
              className="w-full"
              list="marcas-list"
              disabled={disabled}
            />
            <datalist id="marcas-list">
              {MARCAS_COMUNS.map((marca) => (
                <option key={marca} value={marca} />
              ))}
            </datalist>
          </div>
        </div>

        <div>
          <label htmlFor={modeloId} className="block text-sm font-medium text-gray-700 mb-1">
            Modelo <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <Input
            id={modeloId}
            type="text"
            value={formData.modelo}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, modelo: e.target.value }))
              if (errors.modelo) setErrors((prev) => ({ ...prev, modelo: undefined }))
            }}
            placeholder="Fiesta 1.0"
            error={errors.modelo}
            className="w-full"
            disabled={disabled}
          />
        </div>

        <div>
          <label htmlFor={anoId} className="block text-sm font-medium text-gray-700 mb-1">
            Ano <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <select
            id={anoId}
            value={formData.ano}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, ano: e.target.value }))
              if (errors.ano) setErrors((prev) => ({ ...prev, ano: undefined }))
            }}
            className={cn(
              'input',
              'appearance-none bg-white cursor-pointer',
              'pr-10',
              errors.ano && 'border-red-500 focus:ring-red-500'
            )}
            disabled={disabled}
          >
            <option value="">—</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          {errors.ano && <p className="mt-1 text-sm text-red-600">{errors.ano}</p>}
        </div>

        <div>
          <label htmlFor={kmId} className="block text-sm font-medium text-gray-700 mb-1">
            Quilometragem (km) <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <Input
            id={kmId}
            type="text"
            value={formData.km}
            onChange={(e) => {
              const value = e.target.value.replaceAll(/\D/g, '')
              setFormData((prev) => ({ ...prev, km: value }))
              if (errors.km) setErrors((prev) => ({ ...prev, km: undefined }))
            }}
            placeholder="92000"
            error={errors.km}
            className="w-full"
            disabled={disabled}
          />
        </div>

        <div>
          <label htmlFor={combustivelId} className="block text-sm font-medium text-gray-700 mb-1">
            Combustível <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <select
            id={combustivelId}
            value={formData.combustivel}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, combustivel: e.target.value }))
              if (errors.combustivel) setErrors((prev) => ({ ...prev, combustivel: undefined }))
            }}
            className={cn(
              'input',
              'appearance-none bg-white cursor-pointer',
              'pr-10',
              errors.combustivel && 'border-red-500 focus:ring-red-500'
            )}
            disabled={disabled}
          >
            <option value="">—</option>
            {COMBUSTIVEIS.map((comb) => (
              <option key={comb.value} value={comb.value}>
                {comb.label}
              </option>
            ))}
          </select>
          {errors.combustivel && <p className="mt-1 text-sm text-red-600">{errors.combustivel}</p>}
        </div>

        <div className="md:col-span-2">
          <label htmlFor={ultimaInspecaoId} className="block text-sm font-medium text-gray-700 mb-1">
            Data da última inspeção <span className="text-gray-400 text-xs font-normal">(opcional)</span>
          </label>
          <input
            id={ultimaInspecaoId}
            type="date"
            value={formData.ultimaInspecao}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, ultimaInspecao: e.target.value }))
            }}
            className={cn('input', 'w-full', 'max-w-xs')}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <Button type="submit" disabled={isLoading || disabled} className="min-w-[120px]">
          {isLoading ? 'A processar...' : 'Continuar'}
        </Button>
      </div>
    </form>
  )
}
